import { useState } from 'react'
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

interface SortableHeaderProps {
  label: string
  field: string
  currentSort: string
  currentDirection: 'asc' | 'desc'
  onSort: (field: string) => void
  className?: string
}

export default function SortableHeader({ label, field, currentSort, currentDirection, onSort, className = '' }: SortableHeaderProps) {
  const isActive = currentSort === field
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 ${className}`}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <span className="flex flex-col">
          <ChevronUpIcon className={`h-3 w-3 -mb-1 ${isActive && currentDirection === 'asc' ? 'text-blue-600' : 'text-gray-300'}`} />
          <ChevronDownIcon className={`h-3 w-3 ${isActive && currentDirection === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} />
        </span>
      </div>
    </th>
  )
}

export function useSort<T>(defaultField: string, defaultDirection: 'asc' | 'desc' = 'asc') {
  const [sortField, setSortField] = useState(defaultField)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultDirection)

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortData = (data: T[]) => {
    return [...data].sort((a: any, b: any) => {
      let valA = a[sortField]
      let valB = b[sortField]

      // Handle nested fields (e.g., "ingredient.name")
      if (sortField.includes('.')) {
        const parts = sortField.split('.')
        valA = parts.reduce((obj: any, key: string) => obj?.[key], a)
        valB = parts.reduce((obj: any, key: string) => obj?.[key], b)
      }

      // Handle nulls
      if (valA == null && valB == null) return 0
      if (valA == null) return 1
      if (valB == null) return -1

      // Numeric comparison
      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortDirection === 'asc' ? valA - valB : valB - valA
      }

      // String comparison
      const strA = String(valA).toLowerCase()
      const strB = String(valB).toLowerCase()
      const cmp = strA.localeCompare(strB)
      return sortDirection === 'asc' ? cmp : -cmp
    })
  }

  return { sortField, sortDirection, handleSort, sortData }
}
